import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BRAND_NAME, SMS_CONSENT_TEXT, CONSENT_VERSION } from '../config/brand';

const STORAGE_KEY = 'laj_sms_consent_records';
const OPT_IN_ENDPOINT = import.meta.env.VITE_SMS_OPTIN_ENDPOINT;

const formatPhone = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const storeConsentRecord = (record) => {
  try {
    const existing = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
    existing.push(record);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
  } catch (e) {
    // Storage unavailable (private mode, quota); the POST below is the system of record.
  }
};

const SmsOptInForm = () => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  // Always false on mount. Never seeded from storage, props, or defaultChecked —
  // a pre-checked box is exactly what A2P 10DLC review rejects.
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Consent is checked first: we do not accept the phone number without it.
    // The submit button is also disabled until the box is checked, so this is a
    // backstop for keyboard/programmatic submission.
    if (!consent) {
      setError('Please check the consent box to opt in to SMS messages.');
      return;
    }

    const digits = phone.replace(/\D/g, '');
    if (digits.length !== 10) {
      setError('Please enter a valid 10-digit US mobile number.');
      return;
    }

    setError('');
    setIsSubmitting(true);

    const record = {
      name: name.trim() || null,
      phone: `+1${digits}`,
      consent: true,
      consentText: SMS_CONSENT_TEXT,
      consentVersion: CONSENT_VERSION,
      consentedAt: new Date().toISOString(),
      pageUrl: typeof window !== 'undefined' ? window.location.href : 'https://lajdigital.com/sms-optin',
      // IP and user agent are recorded server-side, where they can be trusted.
      source: typeof window !== 'undefined' ? window.location.href : 'https://lajdigital.com/sms-optin',
    };

    storeConsentRecord(record);

    // With an endpoint configured, the server holds the compliance record — a
    // failed POST must not show the visitor a false confirmation.
    if (OPT_IN_ENDPOINT) {
      try {
        const response = await fetch(OPT_IN_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(record),
        });
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          setError(body.error || 'We could not record your opt-in. Please try again in a moment.');
          setIsSubmitting(false);
          return;
        }
      } catch (err) {
        setError('We could not reach our servers. Please check your connection and try again.');
        setIsSubmitting(false);
        return;
      }
    }

    setIsSubmitting(false);
    setIsSubmitted(true);
  };

  return (
    <div id="sms-opt-in" className="mt-12 pt-10 border-t border-gray-800">
      <h2 className="text-xl font-display font-bold text-white mb-2">Sign Up for SMS Updates</h2>
      <p className="text-gray-400 mb-6">
        This is a standalone SMS consent. It is not required to contact us, become a client, or use any
        of our services.
      </p>

      <div className="p-6 md:p-8 bg-gray-900/50 rounded-2xl border border-gray-800">
        {isSubmitted ? (
          <div className="text-center py-8" role="status" aria-live="polite">
            <div className="w-16 h-16 bg-purple-500/20 text-purple-300 rounded-full flex items-center justify-center mx-auto mb-5 text-3xl border border-purple-500/50">
              ✓
            </div>
            <h3 className="text-2xl font-display font-bold text-white mb-3">You&apos;re subscribed!</h3>
            <p className="text-gray-400 font-light">
              You&apos;ll receive a confirmation text shortly. Reply STOP at any time to cancel, or HELP for
              assistance.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6" noValidate autoComplete="off">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="sms-name" className="block text-sm font-medium text-gray-400 mb-2 font-display tracking-wide uppercase">
                  Name <span className="normal-case tracking-normal text-gray-500">(optional)</span>
                </label>
                <input
                  id="sms-name"
                  type="text"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Doe"
                  className="w-full px-5 py-4 bg-gray-950/60 border border-gray-700 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all text-white placeholder-gray-500 font-light text-base"
                />
              </div>
              <div>
                <label htmlFor="sms-phone" className="block text-sm font-medium text-gray-400 mb-2 font-display tracking-wide uppercase">
                  Mobile Number <span className="text-purple-400">*</span>
                </label>
                <input
                  id="sms-phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel-national"
                  required
                  value={phone}
                  onChange={(e) => setPhone(formatPhone(e.target.value))}
                  placeholder="(555) 123-4567"
                  aria-describedby="sms-phone-help"
                  className="w-full px-5 py-4 bg-gray-950/60 border border-gray-700 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all text-white placeholder-gray-500 font-light text-base"
                />
                <p id="sms-phone-help" className="text-xs text-gray-500 mt-2">US mobile numbers only.</p>
              </div>
            </div>

            <label className="flex items-start gap-4 cursor-pointer group">
              <span className="relative flex items-center justify-center mt-1 shrink-0">
                <input
                  type="checkbox"
                  name="sms-consent"
                  autoComplete="off"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  aria-describedby="sms-disclaimers"
                  className="peer appearance-none w-5 h-5 border border-gray-600 rounded bg-gray-950/60 checked:bg-purple-500 checked:border-transparent focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all"
                />
                <svg
                  className="absolute w-3 h-3 text-gray-900 opacity-0 peer-checked:opacity-100 pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                </svg>
              </span>
              <span className="text-sm text-gray-300 font-light leading-relaxed group-hover:text-gray-200 transition-colors">
                {SMS_CONSENT_TEXT}
              </span>
            </label>

            <div id="sms-disclaimers" className="text-xs text-gray-500 space-y-2 leading-relaxed">
              <p>
                <strong className="text-gray-400">What you&apos;ll receive:</strong> responses to your
                inquiries, account and service updates, appointment reminders, and customer support
                messages from {BRAND_NAME}.
              </p>
              <p>
                <strong className="text-gray-400">Frequency:</strong> message frequency varies based on
                your interaction with our services. Message and data rates may apply. Carriers are not
                liable for delayed or undelivered messages.
              </p>
              <p>
                <strong className="text-gray-400">Opt out or get help:</strong> reply STOP to cancel at any
                time, or HELP for assistance.
              </p>
              <p>
                See our{' '}
                <Link to="/terms" className="text-purple-400 hover:text-purple-300 underline">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="text-purple-400 hover:text-purple-300 underline">
                  Privacy Policy
                </Link>
                . We will not sell or share your mobile number with third parties for marketing purposes.
              </p>
            </div>

            {error && (
              <p role="alert" className="text-sm text-red-400 bg-red-900/20 border border-red-500/30 rounded-xl px-4 py-3">
                {error}
              </p>
            )}

            {!consent && (
              // Explains why the button is disabled, so the requirement doesn't
              // read as a broken form.
              <p id="sms-consent-required" className="text-sm text-gray-400 bg-gray-900/60 border border-gray-700 rounded-xl px-4 py-3">
                Check the consent box above to enable sign-up.
              </p>
            )}

            <button
              type="submit"
              disabled={!consent || isSubmitting}
              aria-describedby={!consent ? 'sms-consent-required' : undefined}
              className="btn-neon w-full text-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Sign Up for SMS Updates'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default SmsOptInForm;
